import React, { useState } from "react";
import { AlertTriangle, Check, Info, RefreshCcw, ShoppingCart, Tag, X } from "lucide-react";
import {
  abbreviatePackageType,
  formatUnitPrice,
  getActualOrderQty,
  getMatchingPriceSlab,
  getSlabUnitPrice,
  groupOtherMatches,
} from "utils/bomToolUtils";

const BomOtherDistributors = ({
  rowId,
  isFound,
  activeProduct,
  isExpanded,
  otherMatches,
  distributorErrors = [],
  distributorEmptyResults = [],
  index,
  onRefreshDistributor,
  refreshingDistributors = new Set(),
  itemId,
  onShowSlabs,
  actualQty,
  onSaveActualQty,
  onAddToCart,
  crossSplit,
  onAddSplitToCart,
}) => {
  const [editedQtys, setEditedQtys] = useState({});
  const [appliedSuggestedQtys, setAppliedSuggestedQtys] = useState({});
  const [originalQtys, setOriginalQtys] = useState({});
  const getRowQty = (matchIndex, match) => {
    const edited = editedQtys[matchIndex];
    if (edited !== undefined && edited !== "") return parseInt(edited, 10) || 0;
    return parseInt(actualQty, 10) || parseInt(match?.qty, 10) || 0;
  };
  const handleApplySuggestedQty = (matchIndex, suggestedQty, match) => {
    const currentQty = getRowQty(matchIndex, match);
    setOriginalQtys((prev) => ({
      ...prev,
      [matchIndex]: prev[matchIndex] !== undefined ? prev[matchIndex] : currentQty,
    }));
    setEditedQtys((prev) => ({
      ...prev,
      [matchIndex]: String(suggestedQty ?? ""),
    }));
    setAppliedSuggestedQtys((prev) => ({ ...prev, [matchIndex]: true }));
  };
  const handleRestoreOriginalQty = (matchIndex) => {
    setEditedQtys((prev) => {
      if (originalQtys[matchIndex] === undefined) return prev;
      return { ...prev, [matchIndex]: String(originalQtys[matchIndex]) };
    });
    setOriginalQtys((prev) => {
      if (!(matchIndex in prev)) return prev;
      const next = { ...prev };
      delete next[matchIndex];
      return next;
    });
    setAppliedSuggestedQtys((prev) => {
      if (!(matchIndex in prev)) return prev;
      const next = { ...prev };
      delete next[matchIndex];
      return next;
    });
  };
  const handleToggleSuggestedQty = (matchIndex, suggestedQty, checked, match) => {
    if (checked) {
      handleApplySuggestedQty(matchIndex, suggestedQty, match);
    } else {
      handleRestoreOriginalQty(matchIndex);
    }
  };
  const handleAddMatchToCart = (match, matchIndex) => {
    const qty = getRowQty(matchIndex, match);
    if (!qty || qty <= 0) return;
    const actualQty = getActualOrderQty(match, qty);
    const slab = getMatchingPriceSlab(match, actualQty) ?? match.selectedPrice;
    const unitPrice = getSlabUnitPrice(slab);
    onAddToCart?.({
      ...match,
      qty,
      selectedPrice: slab,
      actualOrderQty: actualQty,
      totalPrice: unitPrice * actualQty,
    });
  };
  const groupedMatches = groupOtherMatches(otherMatches).sort(
    (a, b) => (a.match.totalPrice ?? 0) - (b.match.totalPrice ?? 0),
  );
  // In a cross-distributor split, the table's rows come from the split
  // segments (one per distributor, each carrying its own allocated qty)
  // instead of the plain otherMatches list — same table, same editable RFQ
  // qty / action column, just a different row source plus a Suggested Qty
  // column showing what each distributor was allocated.
  const entries = crossSplit
    ? crossSplit.segments.map((seg, segIdx) => ({
        key: `split-${seg.distributor}-${segIdx}`,
        match: {
          ...seg.product,
          distributor: seg.distributor,
          availability: seg.availability,
          selectedPrice: seg.slab,
        },
        packageOptions: undefined,
        suggestedQty: seg.qty,
      }))
    : groupedMatches.map((group) => ({ ...group, suggestedQty: null }));
  const hasOtherMatchContent =
    groupedMatches.length > 0 ||
    distributorErrors.length > 0 ||
    distributorEmptyResults.length > 0;
  const hasContent = hasOtherMatchContent || !!crossSplit;
  const errorEmptyColSpan = crossSplit ? 7 : 6;
  return (
    <>
      {isExpanded && hasContent && (
        <tr className={index % 2 === 0 ? "bg-slate-50" : "bg-slate-100/70"}>
          <td></td>
          <td></td>
          <td colSpan={12} className="px-4 py-4">
            <div style={{ width: "84%" }}>
            <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      {[
                        "SKU",
                        "Make",
                        "Distributor",
                        "HS Code",
                        "Date Code",
                        "RFQ QTY",
                        ...(crossSplit ? ["Suggested Qty"] : []),
                        "Stock",
                        "MOQ",
                        "MUL",
                        "Price",
                        "Action",
                      ].map((col) => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {distributorErrors.map((err, errIndex) => {
                      const refreshKey = `${itemId}-${err.distributor}`;
                      const isRefreshing = refreshingDistributors.has(refreshKey);
                      return (
                        <tr
                          key={`${rowId}-err-${errIndex}`}
                          className="bg-red-50"
                        >
                          <td
                            style={{ width: "230px" }}
                            className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap"
                          >
                            -
                          </td>
                          <td
                            style={{ width: "290px" }}
                            className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap"
                          >
                            -
                          </td>
                          <td className="px-3 py-2 text-sm font-medium text-gray-800 whitespace-nowrap">
                            {err.distributor}
                          </td>
                          <td colSpan={errorEmptyColSpan} className="px-3 py-2">
                            <div className="flex items-center gap-1.5 text-sm text-red-600">
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                              <span>{err.error_message}</span>
                            </div>
                          </td>
                          <td >

                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                             {onRefreshDistributor && (
                              <button
                                onClick={() => onRefreshDistributor(err.distributor)}
                                disabled={isRefreshing}
                                className="inline-flex items-center gap-1 border border-s360-violet-100 bg-s360-violet-50 hover:bg-s360-violet-50 text-s360-violet-600 px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={`Refresh ${err.distributor}`}
                              >
                                <RefreshCcw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {entries.map((entry, matchIndex) => {
                      const { match, suggestedQty } = entry;
                      const matchAvailability = match.availability;
                      const isNoStock =
                        matchAvailability === null ||
                        matchAvailability === undefined ||
                        matchAvailability === "" ||
                        matchAvailability === "0" ||
                        matchAvailability === 0 ||
                        matchAvailability === "None" ||
                        matchAvailability === "N/A";
                      const rfqVal = parseInt(editedQtys[matchIndex] ?? (actualQty ?? match.qty ?? ""), 10) || 0;
                      const availVal = parseInt(matchAvailability, 10);
                      const isPartial = !isNoStock && !isNaN(availVal) && availVal > 0 && availVal < rfqVal;
                      const isInStock = !isNoStock && !isNaN(availVal) && availVal >= rfqVal;
                      const rowColors = isNoStock
                        ? "bg-[#FFF0F0] hover:bg-[#FFE0E0]"
                        : isPartial
                          ? "bg-[#DBEAFE] hover:bg-[#BFDBFE]"
                          : isInStock
                            ? "bg-[#F0FDF4] hover:bg-[#DCFCE7]"
                            : matchIndex % 2 === 0
                              ? "bg-white hover:bg-s360-stripe"
                              : "bg-slate-50 hover:bg-s360-stripe";
                      return (
                        <tr
                          key={`${rowId}-${entry.key}`}
                          className={rowColors}
                        >
                          <td
                            style={{ width: "230px" }}
                            className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap"
                          >
                            {match.part_number ?? "-"}
                          </td>
                          <td
                            style={{ width: "290px" }}
                            className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap"
                          >
                            {match.manufacturer ?? "-"}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                            {match.price?.package_type
                              ? `${match.distributor} - ${abbreviatePackageType(match.price.package_type)}`
                              : (match.distributor ?? "-")}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                            {match.hts_code ?? "-"}
                          </td>
                          <td
                            style={{ width: "100px" }}
                            className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap"
                          >
                            {match.date_code || "-"}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                            <input
                              type="number"
                              min="1"
                              value={editedQtys[matchIndex] ?? (actualQty ?? match.qty ?? "")}
                              onChange={(e) =>
                                setEditedQtys((prev) => ({
                                  ...prev,
                                  [matchIndex]: e.target.value,
                                }))
                              }
                              className="w-20 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-s360-violet bg-white"
                            />
                            {appliedSuggestedQtys[matchIndex] && originalQtys[matchIndex] !== undefined && (
                              <div className="mt-1 text-[11px] text-slate-500 whitespace-nowrap">
                                Original Qty: {Number(originalQtys[matchIndex]).toLocaleString()}
                              </div>
                            )}
                          </td>
                          {crossSplit && (
                            <td className="px-3 py-2 text-sm font-semibold text-s360-violet whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <span>{suggestedQty?.toLocaleString() ?? "-"}</span>
                                {suggestedQty != null && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleToggleSuggestedQty(
                                          matchIndex,
                                          suggestedQty,
                                          !appliedSuggestedQtys[matchIndex],
                                          match,
                                        )
                                      }
                                      className={`inline-flex items-center justify-center rounded border transition-colors ${
                                        appliedSuggestedQtys[matchIndex]
                                          ? "bg-s360-violet border-s360-violet text-white"
                                          : "bg-white border-gray-300 text-gray-300 hover:border-s360-violet hover:text-s360-violet"
                                      }`}
                                      title="Apply suggested qty to RFQ Qty"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    {appliedSuggestedQtys[matchIndex] && (
                                      <button
                                        type="button"
                                        onClick={() => handleRestoreOriginalQty(matchIndex)}
                                        className="inline-flex items-center justify-center text-s360-danger hover:text-[#a00d19]"
                                        title="Restore original RFQ qty"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          )}
                          <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                            {isNoStock ? (
                              <span className="text-red-500 font-medium">
                                No Stock
                              </span>
                            ) : (
                              <span className="text-green-600 font-medium">
                                {matchAvailability}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                            {match.selectedPrice?.min_qty ?? "-"}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                            {match.price?.multiple ?? "-"}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap font-medium">
                            {formatUnitPrice(match.selectedPrice) != null
                              ? `${match.price?.currency ?? match.selectedPrice?.currency ?? ""} ${formatUnitPrice(match.selectedPrice)}`.trim()
                              : "-"}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {match.price?.price_list?.length > 0 && onShowSlabs && (
                                <button
                                  onClick={() => onShowSlabs(match, entry.packageOptions)}
                                  className="inline-flex items-center gap-1 border border-s360-violet-100 bg-s360-violet-50 hover:bg-s360-violet-50 text-s360-violet-600 px-2 py-1 rounded text-xs font-medium transition-colors"
                                  title="View price slabs"
                                >
                                  <Tag className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {onAddToCart && !isNoStock && (
                                <button
                                  onClick={() => handleAddMatchToCart(match, matchIndex)}
                                  className="inline-flex items-center gap-1 bg-s360-violet hover:bg-[#14304d] text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                                  title="Add to cart"
                                >
                                  <ShoppingCart className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {distributorEmptyResults.map((empty, emptyIndex) => {
                      const refreshKey = `${itemId}-${empty.distributor}`;
                      const isRefreshing = refreshingDistributors.has(refreshKey);
                      return (
                        <tr
                          key={`${rowId}-empty-${emptyIndex}`}
                          className="bg-amber-50"
                        >
                          <td
                            style={{ width: "230px" }}
                            className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap"
                          >
                            -
                          </td>
                          <td
                            style={{ width: "290px" }}
                            className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap"
                          >
                            -
                          </td>
                          <td className="px-3 py-2 text-sm font-medium text-gray-800 whitespace-nowrap">
                            {empty.distributor}
                          </td>
                          <td colSpan={errorEmptyColSpan} className="px-3 py-2">
                            <div className="flex items-center gap-1.5 text-sm text-amber-700">
                              <Info className="w-3.5 h-3.5 shrink-0" />
                              <span>No results found</span>
                            </div>
                          </td>
                          <td></td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {onRefreshDistributor && (
                              <button
                                onClick={() => onRefreshDistributor(empty.distributor)}
                                disabled={isRefreshing}
                                className="inline-flex items-center gap-1 border border-s360-violet-100 bg-s360-violet-50 hover:bg-s360-violet-50 text-s360-violet-600 px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={`Refresh ${empty.distributor}`}
                              >
                                <RefreshCcw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {crossSplit && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-slate-200 bg-slate-50">
                  <span className="inline-flex items-center gap-1 text-[11px] text-s360-fg3">
                    <Info className="w-3 h-3 shrink-0" />
                    This will place {crossSplit.segments.length} separate orders, one per distributor
                    {" "}- covering {(crossSplit.coveredQty ?? 0).toLocaleString()} of{" "}
                    {(crossSplit.requiredQty ?? 0).toLocaleString()}
                    {crossSplit.shortfall > 0 && (
                      <span className="text-s360-danger">
                        {" "}({crossSplit.shortfall.toLocaleString()} short)
                      </span>
                    )}
                  </span>
                  <div className="flex flex-col items-end gap-1">
                    <button
                      onClick={() =>
                        onAddSplitToCart?.(
                          entries.map((entry, entryIndex) => getRowQty(entryIndex, entry.match)),
                        )
                      }
                      className="inline-flex items-center gap-1.5 bg-s360-violet hover:bg-s360-violet-600 text-white px-3 py-1.5 rounded text-xs font-semibold transition-colors"
                      title="Add every distributor's RFQ qty to the cart"
                    >
                      <ShoppingCart className="w-4 h-4" /> Add all to cart
                    </button>
                    <span className="text-[11px] text-s360-fg3">
                      Note: RFQ Qty of each distributor is taken into the cart
                    </span>
                  </div>
                </div>
              )}
            </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default BomOtherDistributors;
